import { RdfObject } from "./rdf-api.js";
const TYPE = "http://www.w3.org/1999/02/22-rdf-syntax-ns#type";
const HAS_FIRST = "http://www.w3.org/1999/02/22-rdf-syntax-ns#first";
const HAS_REST = "http://www.w3.org/1999/02/22-rdf-syntax-ns#rest";
const RDF_NIL = "http://www.w3.org/1999/02/22-rdf-syntax-ns#nil";
/**
 * Bind resource to particular {@link RdfSource} to make operation easier.
 */
export class RdfSourceWrap {
    iri;
    source;
    constructor(iri, source) {
        this.iri = iri;
        this.source = source;
    }
    static forIri(resource, source) {
        return new RdfSourceWrap(resource, source);
    }
    async property(predicate) {
        return this.source.property(this.iri, predicate);
    }
    async reverseProperty(predicate) {
        return this.source.reverseProperty(predicate, this.iri);
    }
    /**
     * Resolve lists with rdf:first, rdf:rest
     */
    async propertyExtended(predicate) {
        const values = await this.property(predicate);
        const result = [];
        for (const value of values) {
            if (RdfObject.isNotNode(value)) {
                // As the value is not a node it can not represent a list.
                result.push(value);
                continue;
            }
            const hasFirst = await this.source.property(value.value, HAS_FIRST);
            if (hasFirst.length === 0) {
                // It is an entity.
                result.push(value);
            }
            else {
                // It is a list.
                await this.loadList(result, value);
            }
        }
        return result;
    }
    async loadList(collector, node) {
        const hasFirst = await this.source.property(node.value, HAS_FIRST);
        if (hasFirst.length !== 1) {
            throw new Error(`Invalid number (${hasFirst.length}) rdf:head for ${node.value}`);
        }
        const first = hasFirst[0];
        if (RdfObject.isNode(first)) {
            collector.push(first);
        }
        const hasRest = await this.source.property(node.value, HAS_REST);
        if (hasRest.length !== 1) {
            throw new Error(`Invalid number rdf:rest for ${node.value}`);
        }
        const rest = hasRest[0];
        if (RdfObject.isNode(rest)) {
            if (rest.value === RDF_NIL) {
                return;
            }
            await this.loadList(collector, rest);
        }
    }
    async literal(predicate) {
        const values = await this.property(predicate);
        if (values.length == 0) {
            return null;
        }
        for (const value of values) {
            if (RdfObject.isLiteral(value)) {
                return value;
            }
        }
        return null;
    }
    async literals(predicate) {
        const properties = await this.property(predicate);
        return properties.filter(RdfObject.isLiteral);
    }
    async reverseNode(predicate) {
        const values = await this.reverseProperty(predicate);
        for (const value of values) {
            return value.value;
        }
        return null;
    }
    async reverseNodes(predicate) {
        return (await this.reverseProperty(predicate))
            .filter(RdfObject.isNode)
            .map((value) => value.value);
    }
    async node(predicate) {
        const values = await this.property(predicate);
        for (const value of values) {
            if (RdfObject.isNode(value)) {
                return value.value;
            }
        }
        return null;
    }
    async nodes(predicate) {
        return (await this.property(predicate))
            .filter(RdfObject.isNode)
            .map((value) => value.value);
    }
    async nodesExtended(predicate) {
        return (await this.propertyExtended(predicate))
            .filter(RdfObject.isNode)
            .map((value) => value.value);
    }
    async types() {
        return await this.nodes(TYPE);
    }
    async languageString(predicate) {
        const literals = await this.literals(predicate);
        if (literals.length === 0) {
            return null;
        }
        const result = {};
        for (const title of literals) {
            result[title.language ?? ""] = String(title.value);
        }
        return result;
    }
}
export class RdfMemorySourceWrap {
    iri;
    source;
    constructor(iri, source) {
        this.iri = iri;
        this.source = source;
    }
    static forIri(resource, source) {
        return new RdfMemorySourceWrap(resource, source);
    }
    property(predicate) {
        return this.source.property(this.iri, predicate);
    }
    reverseProperty(predicate) {
        return this.source.reverseProperty(predicate, this.iri);
    }
    /**
     * Resolve lists with rdf:first, rdf:rest
     */
    propertyExtended(predicate) {
        const values = this.property(predicate);
        const result = [];
        for (const value of values) {
            if (RdfObject.isNotNode(value)) {
                // As the value is not a node it can not represent a list.
                result.push(value);
                continue;
            }
            const hasFirst = this.source.property(value.value, HAS_FIRST);
            if (hasFirst.length === 0) {
                // It is an entity.
                result.push(value);
            }
            else {
                // It is a list.
                this.loadList(result, value);
            }
        }
        return result;
    }
    loadList(collector, node) {
        const hasFirst = this.source.property(node.value, HAS_FIRST);
        if (hasFirst.length !== 1) {
            throw new Error(`Invalid number (${hasFirst.length}) rdf:head for ${node.value}`);
        }
        const first = hasFirst[0];
        if (RdfObject.isNode(first)) {
            collector.push(first);
        }
        const hasRest = this.source.property(node.value, HAS_REST);
        if (hasRest.length !== 1) {
            throw new Error(`Invalid number rdf:rest for ${node.value}`);
        }
        const rest = hasRest[0];
        if (RdfObject.isNode(rest)) {
            if (rest.value === RDF_NIL) {
                return;
            }
            this.loadList(collector, rest);
        }
    }
    literal(predicate) {
        const values = this.property(predicate);
        if (values.length == 0) {
            return null;
        }
        for (const value of values) {
            if (RdfObject.isLiteral(value)) {
                return value;
            }
        }
        return null;
    }
    literals(predicate) {
        const properties = this.property(predicate);
        return properties.filter(RdfObject.isLiteral);
    }
    reverseNode(predicate) {
        const values = this.reverseProperty(predicate);
        for (const value of values) {
            return value.value;
        }
        return null;
    }
    reverseNodes(predicate) {
        return (this.reverseProperty(predicate))
            .filter(RdfObject.isNode)
            .map((value) => value.value);
    }
    node(predicate) {
        const values = this.property(predicate);
        for (const value of values) {
            if (RdfObject.isNode(value)) {
                return value.value;
            }
        }
        return null;
    }
    nodes(predicate) {
        return (this.property(predicate))
            .filter(RdfObject.isNode)
            .map((value) => value.value);
    }
    nodesExtended(predicate) {
        return (this.propertyExtended(predicate))
            .filter(RdfObject.isNode)
            .map((value) => value.value);
    }
    types() {
        return this.nodes(TYPE);
    }
    languageString(predicate) {
        const literals = this.literals(predicate);
        if (literals.length === 0) {
            return null;
        }
        const result = {};
        for (const title of literals) {
            result[title.language ?? ""] = String(title.value);
        }
        return result;
    }
}
//# sourceMappingURL=rdf-source-wrap.js.map